/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Pair-potential strategy for the crystal MD engine. Cloned out of the
 * molecule MD's non-bonded kernel so we can swap potentials without
 * touching the integrator or neighbor list.
 *
 * Each implementation receives the squared distance and the two atoms'
 * intrinsic parameters (sigma/epsilon today; richer parameter packs for
 * Buckingham/EAM later) and returns the pair energy plus the scalar
 * factor `fij = (virial / rsq) * GF_CONVERSION_CONSTANT` that the kernel
 * multiplies onto the displacement vector to get the force.
 *
 * Why a strategy here and not on the molecule side? The molecule MD has
 * organic-chemistry-specific tweaks (gas/solid sigma hack, arithmetic-
 * mean combining rules) baked into a single kernel. Crystals are a
 * different beast — ionic / covalent / metallic crystals each want a
 * different functional form. Starting with a strategy means each new
 * force field is a self-contained file, not a conditional branch.
 */
import { Atom } from '../Atom.ts';
import { GF_CONVERSION_CONSTANT } from '../physicalConstants.ts';

/**
 * Result of evaluating a pair potential at a single (i, j, r) sample.
 *
 * - `energy`: Vij contribution to the system's potential energy. Caller
 *   accumulates these across all neighbor pairs.
 * - `virial`: Vij + r * dVij/dr — the per-pair contribution to the
 *   pressure-virial sum. (For LJ this collapses to `Vij + sr12*meanEps`
 *   because the r·dV/dr term simplifies via the sr12 pre-factor.)
 * - `fScalar`: scalar factor s such that the cartesian force on atom i
 *   is `s * (ri - rj)` — already in acceleration-compatible units
 *   (pre-multiplied by GF_CONVERSION_CONSTANT). The kernel divides by
 *   mass; it never derives the force from `virial`, so a potential is
 *   free to return whatever functional form it likes.
 */
export interface PairResult {
  energy: number;
  virial: number;
  fScalar: number;
}

/**
 * Strategy interface — one method, called once per pair-within-cutoff
 * by the kernel. Implementations are stateless (parameters come from
 * the atoms themselves); the strategy object itself just dispatches.
 */
export interface PairPotential {
  /** Short label for tooling (UI dropdown, logs). */
  readonly name: string;

  /**
   * Compute energy + virial + force scalar for a single pair. `rCutSq`
   * is the squared cutoff distance for this pair, so the potential can
   * shift itself to vanish smoothly at the cutoff. Returns null when
   * the pair contributes nothing; the kernel then skips it.
   */
  evaluate(ai: Atom, aj: Atom, rsq: number, rCutSq: number): PairResult | null;
}

/**
 * Shifted-force 6-12 Lennard-Jones evaluated from EXPLICIT combined
 * parameters (σ_mean, ε_mean already mixed by the caller). The single
 * source of truth for the shifted-force LJ used by both
 * `LennardJonesPotential` (host-host, reading σ/ε off the atoms) and the
 * guest-host overlay in CrystalPairInteractions (which must supply the
 * host's σ/ε from a UFF reference, because the host atom's stored σ/ε
 * are clobbered to a neighbor-list cutoff length by the covalent /
 * metallic force fields). Keeping one implementation means the
 * F = −∇V guarantee is verified in one place.
 *
 * The raw potential is shifted so that BOTH the energy and the force
 * vanish smoothly at the cutoff rc:
 *   V_sf(r) = V(r) − V(rc) + (r − rc)·F(rc)
 *   F_sf(r) = F(r) − F(rc)
 * A bare truncation leaves a step in the force at rc; every pair that
 * crosses the cutoff then pumps or drains a little energy, so a
 * hard-cut LJ slowly violates energy conservation. Shifting the force
 * removes that step — the cutoff can be short without that penalty.
 */
export function shiftedForceLJ(sigmaMean: number, epsilonMean: number, rsq: number, rCutSq: number): PairResult {
  const sigmaSq = sigmaMean * sigmaMean;
  const fourEps = 4 * epsilonMean;
  const a24Eps = 24 * epsilonMean; // 24ε — prefactor of the radial force

  // Cutoff shift references (same for both branches). V'(rc) = −F(rc), so
  // the shifted-force energy carries a +(r − rc)·F(rc) linear term.
  const sr2c = sigmaSq / rCutSq;
  const sr6c = sr2c * sr2c * sr2c;
  const sr12c = sr6c * sr6c;
  const energyAtCut = fourEps * (sr12c - sr6c);
  const rc = Math.sqrt(rCutSq);
  const forceAtCut = (a24Eps * (2 * sr12c - sr6c)) / rc; // F(rc)

  // Shifted-force V and its radial force F = −dV/dr at a separation, with
  // NO core cap. (forceOverR · rsq = F · r; F/r is what scales the
  // displacement vector.)
  const evalSF = (rsq2: number): { V: number; F: number; r: number } => {
    const sr2 = sigmaSq / rsq2;
    const sr6 = sr2 * sr2 * sr2;
    const sr12 = sr6 * sr6;
    const r = Math.sqrt(rsq2);
    const V = fourEps * (sr12 - sr6) - energyAtCut + (r - rc) * forceAtCut;
    const F = (a24Eps * (2 * sr12 - sr6)) / r - forceAtCut; // F_sf(r) = −dV/dr
    return { V, F, r };
  };

  // Deep-overlap guard. A raw 6-12 force diverges as r → 0, which would
  // blow up MD if a guest is ever placed on top of a host atom. Below a
  // switch radius r_sw = σ/√2 (sr² = 2) we replace the wall with its
  // tangent line: V is LINEAR in r with slope −F(r_sw), so the force is
  // constant (bounded) AND F = −∇V holds EXACTLY. The old code froze the
  // energy at a constant while leaving F non-zero — an F ≠ −∇V step that
  // injects energy under NVE (and breaks gradient-descent relaxation),
  // exactly in the band an interstitial guest occupies (it sits closer to
  // the host than r_sw, since σ_mean often exceeds the lattice spacing).
  const rswSq = sigmaSq / 2; // sr² = 2 boundary
  let energy: number;
  let fOverR: number;
  if (rsq >= rswSq) {
    const { V, F, r } = evalSF(rsq);
    energy = V;
    fOverR = F / r;
  } else {
    const { V: Vsw, F: Fsw } = evalSF(rswSq);
    const r = Math.sqrt(rsq);
    const rsw = Math.sqrt(rswSq);
    // V(r) = V(r_sw) − F(r_sw)·(r − r_sw);  −dV/dr = F(r_sw) (constant).
    energy = Vsw - Fsw * (r - rsw);
    fOverR = Fsw / r;
  }
  const fScalar = fOverR * GF_CONVERSION_CONSTANT;
  // Pair virial r·F_sf(r), kept for the pressure readout.
  const virial = fOverR * rsq;

  return { energy, virial, fScalar };
}

/**
 * Default crystal pair potential: a SHIFTED-FORCE 6-12 Lennard-Jones
 * with Lorentz-Berthelot combining rules (arithmetic mean for sigma,
 * geometric mean for epsilon). Thin wrapper over `shiftedForceLJ`.
 */
export class LennardJonesPotential implements PairPotential {
  readonly name = 'Lennard-Jones';

  evaluate(ai: Atom, aj: Atom, rsq: number, rCutSq: number): PairResult | null {
    return shiftedForceLJ(0.5 * (ai.sigma + aj.sigma), Math.sqrt(ai.epsilon * aj.epsilon), rsq, rCutSq);
  }
}

/** Buckingham parameters for one element pair: V(r) = A·e^(−r/ρ) − C/r⁶. */
export interface BuckinghamPair {
  /** Born-Mayer pre-factor, eV. */
  A: number;
  /** Born-Mayer softness length, Å. */
  rho: number;
  /** r⁻⁶ dispersion coefficient, eV·Å⁶ (0 ⇒ no dispersion term). */
  C: number;
}

/** Order-independent table key for an element pair: ('O','Mg') → 'Mg-O'. */
const pairKey = (e1: string, e2: string): string => (e1 < e2 ? e1 + '-' + e2 : e2 + '-' + e1);

/**
 * Buckingham (Born-Mayer + r⁻⁶ dispersion) potential for ionic crystals:
 *
 *     V(r) = A·e^(−r/ρ) − C/r⁶
 *
 * Unlike Lennard-Jones the parameters are NOT per-atom with a combining
 * rule — they are tabulated per element PAIR (Mg-O, O-O, …), the way
 * ionic force fields are published. A pair absent from the table —
 * cation-cation, typically — has no short-range term and `evaluate`
 * returns null: such pairs are held apart by the Coulomb/Ewald sum alone.
 *
 * Like LennardJonesPotential this is SHIFTED-FORCE: V and F are shifted
 * to vanish smoothly at the cutoff. It additionally guards the Buckingham
 * catastrophe — A·e^(−r/ρ) is bounded but −C/r⁶ diverges, so V → −∞ as
 * r → 0; below `rMin` the attractive term is frozen, leaving a finite,
 * monotonic Born-Mayer wall.
 */
export class BuckinghamPotential implements PairPotential {
  readonly name = 'Buckingham';
  private readonly pairs = new Map<string, BuckinghamPair>();
  private readonly rMin: number;
  private readonly rMinSq: number;

  constructor(pairs: Record<string, BuckinghamPair>, rMin: number = 1.0) {
    for (const [key, p] of Object.entries(pairs)) {
      const [e1, e2] = key.split('-');
      this.pairs.set(pairKey(e1, e2), p);
    }
    this.rMin = rMin;
    this.rMinSq = rMin * rMin;
  }

  evaluate(ai: Atom, aj: Atom, rsq: number, rCutSq: number): PairResult | null {
    const p = this.pairs.get(pairKey(ai.elementSymbol, aj.elementSymbol));
    // No tabulated short-range term — e.g. a cation-cation pair, which an
    // ionic force field leaves to the Coulomb (Ewald) sum alone.
    if (!p) return null;
    const { A, rho, C } = p;
    const r = Math.sqrt(rsq);

    // Raw potential V(r) and radial force F(r) = −dV/dr at the actual
    // separation (F > 0 ⇒ repulsive).
    const expTerm = A * Math.exp(-r / rho);
    let rawEnergy: number;
    let force: number;
    if (r < this.rMin) {
      // Buckingham-catastrophe guard. −C/r⁶ outruns the bounded
      // exponential as r → 0; freeze the attraction at its rMin value (a
      // constant ⇒ no force) so the inner wall is pure Born-Mayer.
      const cFrozen = C / (this.rMinSq * this.rMinSq * this.rMinSq);
      rawEnergy = expTerm - cFrozen;
      force = expTerm / rho;
    } else {
      const c6 = C / (rsq * rsq * rsq); // C/r⁶
      rawEnergy = expTerm - c6;
      force = expTerm / rho - (6 * c6) / r; // (A/ρ)e^(−r/ρ) − 6C/r⁷
    }

    // Shifted-force reference at the cutoff (always well past rMin).
    const rc = Math.sqrt(rCutSq);
    const expCut = A * Math.exp(-rc / rho);
    const c6Cut = C / (rCutSq * rCutSq * rCutSq);
    const energyAtCut = expCut - c6Cut;
    const forceAtCut = expCut / rho - (6 * c6Cut) / rc;

    // V_sf(r) = V(r) − V(rc) + (r − rc)·F(rc);  F_sf(r) = F(r) − F(rc).
    const energy = rawEnergy - energyAtCut + (r - rc) * forceAtCut;
    const fOverR = (force - forceAtCut) / r;
    return { energy, virial: fOverR * rsq, fScalar: fOverR * GF_CONVERSION_CONSTANT };
  }
}

/** Morse pair parameters: V(r) = D·[1 − exp(−α(r−r₀))]² − D. */
export interface MorsePair {
  /** Well depth, eV (V at r = r₀ is −D). */
  D: number;
  /** Inverse width, Å⁻¹. */
  alpha: number;
  /** Equilibrium pair distance, Å. */
  r0: number;
}

/**
 * Morse potential — a pairwise stand-in for the proper many-body EAM
 * physics that metallic crystals genuinely want.
 *
 *     V(r) = D · [exp(−2α(r−r₀)) − 2·exp(−α(r−r₀))]
 *
 * For metals this gets the cohesive energy and lattice constant right
 * when parameters are fit to those quantities (e.g. Girifalco-Weizer
 * 1959 for the FCC metals), and gives qualitatively-correct vibrational
 * dynamics. It does NOT capture the many-body electron-density
 * embedding that drives the correct elastic anisotropy (the Cauchy
 * relation C₁₂ = C₄₄ holds for any pairwise potential; real metals
 * have C₁₂ ≠ C₄₄), nor the right vacancy/surface energies. That is the
 * EAM/Finnis-Sinclair regime — a many-body interface beyond the strictly
 * pairwise `PairPotential.evaluate(ai, aj, rsq, …)` we have today.
 *
 * Like LennardJonesPotential / BuckinghamPotential this is shifted-force:
 * V and F are shifted to vanish smoothly at the cutoff. Morse decays
 * exponentially in r so even a modest ~5 Å cutoff captures essentially
 * the entire well; the shift removes the small residual cleanly.
 */
export class MorsePotential implements PairPotential {
  readonly name = 'Morse';
  private readonly pairs = new Map<string, MorsePair>();

  constructor(pairs: Record<string, MorsePair>) {
    for (const [key, p] of Object.entries(pairs)) {
      const [e1, e2] = key.split('-');
      this.pairs.set(pairKey(e1, e2), p);
    }
  }

  evaluate(ai: Atom, aj: Atom, rsq: number, rCutSq: number): PairResult | null {
    const p = this.pairs.get(pairKey(ai.elementSymbol, aj.elementSymbol));
    if (!p) return null;
    const { D, alpha, r0 } = p;
    const r = Math.sqrt(rsq);

    // Raw Morse V(r) and radial force F(r) = −dV/dr (F > 0 ⇒ repulsive).
    // With u = exp(−α(r−r₀)):
    //   V(r) = D·(u² − 2u)            (V = −D at r = r₀; V → 0 as r → ∞)
    //   F(r) = 2αD·u·(u − 1)           (F = 0 at r = r₀; F > 0 for r < r₀)
    const u = Math.exp(-alpha * (r - r0));
    const rawEnergy = D * (u * u - 2 * u);
    const force = 2 * alpha * D * u * (u - 1);

    // Shifted-force reference at the cutoff. Morse has no catastrophe —
    // V is bounded by D·exp(2αr₀) as r → 0, so no inner guard needed.
    const rc = Math.sqrt(rCutSq);
    const uc = Math.exp(-alpha * (rc - r0));
    const energyAtCut = D * (uc * uc - 2 * uc);
    const forceAtCut = 2 * alpha * D * uc * (uc - 1);

    // V_sf(r) = V(r) − V(rc) + (r − rc)·F(rc);  F_sf(r) = F(r) − F(rc).
    const energy = rawEnergy - energyAtCut + (r - rc) * forceAtCut;
    const fOverR = (force - forceAtCut) / r;
    return { energy, virial: fOverR * rsq, fScalar: fOverR * GF_CONVERSION_CONSTANT };
  }
}

// ─── Many-body (EAM-family) potentials ────────────────────────────────
//
// Metallic crystals genuinely want a potential of the EAM form:
//
//     E_total = Σ_i F(ρ_i) + Σ_{i<j} φ(r_ij),    ρ_i = Σ_{j≠i} ψ(r_ij)
//
// — a pair part φ plus a per-atom embedding F of the local electron
// density ρ_i. The embedding term captures the many-body "screening"
// that pairwise potentials cannot, and it's what gives real metals
// their non-pairwise elastic anisotropy (C₁₂ ≠ C₄₄), correct vacancy
// formation energies and surface energies.
//
// The kernel needs TWO passes for this form: pass 1 sums ψ into per-
// atom ρ, then F(ρ_i) and F′(ρ_i) are evaluated per atom, then pass 2
// computes the force, which combines the pair-direct φ′(r_ij) with the
// embedding chain-rule term (F′(ρ_i) + F′(ρ_j))·ψ′(r_ij). The kernel
// dispatches to this two-pass path when the crystal has a
// `manyBodyPotential` installed instead of a plain `PairPotential`.
//
// The Daw-Baskes EAM, Finnis-Sinclair, Sutton-Chen, and the TBSMA
// family all share this functional form — they differ only in the
// specific F / ρ / φ functions. `SuttonChenPotential` below is the
// simplest analytic instance; a future tabulated `FoilesEAMPotential`
// would slot into the same interface.
export interface ManyBodyPotential {
  /** Short label for tooling (UI dropdown, logs). */
  readonly name: string;

  /**
   * Pass-1 (density). Contribution to atom i's local density ρ_i from
   * neighbor j at squared separation rsq, and vice versa. For
   * elemental crystals psiOnI === psiOnJ; alloys may differ. Returns
   * null when the pair contributes nothing — the kernel then skips it.
   */
  densityContribution(ai: Atom, aj: Atom, rsq: number, rCutSq: number): { psiOnI: number; psiOnJ: number } | null;

  /**
   * Per-atom embedding F(ρ) and dF/dρ at the local density `rho`.
   * Called once per atom between the density pass and the force pass;
   * `dFdRho` is fed back into pairForce() for the chain-rule force term.
   */
  embedding(a: Atom, rho: number): { F: number; dFdRho: number };

  /**
   * Pass-2 (forces). Total per-pair contribution to energy (the pair
   * part φ — F is added separately by the kernel via embedding()) and
   * to the radial-force scalar, combining φ′(r_ij) with the embedding
   * chain rule (F′(ρ_i)+F′(ρ_j))·ψ′(r_ij). `fScalar` follows the same
   * convention as PairPotential — cartesian force on i is fScalar·(rᵢ−rⱼ),
   * already pre-multiplied by GF_CONVERSION_CONSTANT for acceleration units.
   */
  pairForce(
    ai: Atom,
    aj: Atom,
    rsq: number,
    rCutSq: number,
    dFdRhoI: number,
    dFdRhoJ: number,
  ): { energy: number; fScalar: number } | null;

  /**
   * Optional: multiply the whole energy surface (F and φ, hence every
   * force) by `factor`. Used to renormalize cohesion at the kernel's
   * actual cutoff — see eamParameters.EamPack.cohesionEv. Potentials whose
   * cutoffs are built into the fit (Finnis–Sinclair) omit it.
   */
  scaleEnergy?(factor: number): void;
}

/** Sutton-Chen parameters for one element. */
export interface SuttonChenParams {
  /** Energy scale, eV. */
  epsilon: number;
  /** Length scale, Å (the original paper uses the FCC lattice constant). */
  a: number;
  /** Embedding dimensionless prefactor. */
  c: number;
  /** Density-function exponent. */
  m: number;
  /** Pair-function exponent. */
  n: number;
}

/**
 * Sutton-Chen (1990) — the simplest analytic EAM-family potential.
 * Phil. Mag. Lett. 61, 139.
 *
 *     ψ(r) = (a/r)^m        ρ_i = Σ_j ψ(r_ij)
 *     F(ρ) = −c·ε·√ρ        (binding from the embedding)
 *     φ(r) = ε·(a/r)^n      (pair repulsion at short r)
 *
 * Five parameters per element (ε, a, c, m, n) fit to reproduce cohesion,
 * lattice constant, and bulk modulus. The original paper covers the FCC
 * metals (Cu, Ag, Au, Ni, Pd, Pt, Pb, Al, Ir, Rh). Shifted-force is
 * applied to BOTH ψ and φ so the kernel cutoff leaves no force step.
 */
export class SuttonChenPotential implements ManyBodyPotential {
  readonly name = 'Sutton-Chen';
  /** Element-keyed (used for the per-atom embedding F = −c·ε·√ρ). */
  private readonly elemParams = new Map<string, SuttonChenParams>();
  /**
   * Pair-keyed pair/density parameters. For elemental packs each
   * key is just '<el>-<el>'. For alloys, cross-species entries are
   * synthesized from the elemental packs via the Rafii-Tabar &
   * Sutton (1991) mixing rules:
   *   ε_AB = √(ε_AA·ε_BB),   a_AB = (a_AA+a_BB)/2,
   *   m_AB = (m_AA+m_BB)/2,  n_AB = (n_AA+n_BB)/2.
   */
  private readonly pairParams = new Map<string, { a: number; m: number; n: number; epsilon: number }>();

  constructor(params: Record<string, SuttonChenParams>) {
    for (const [el, p] of Object.entries(params)) this.elemParams.set(el, p);
    // Build pair-keyed mixed parameters for every element combination.
    // Self-pairs (A,A) reproduce the elemental values exactly — so the
    // refactor is backward-compatible with single-element packs.
    const elements = Object.keys(params);
    for (let i = 0; i < elements.length; i++) {
      for (let j = i; j < elements.length; j++) {
        const pa = params[elements[i]];
        const pb = params[elements[j]];
        this.pairParams.set(pairKey(elements[i], elements[j]), {
          epsilon: Math.sqrt(pa.epsilon * pb.epsilon),
          a: (pa.a + pb.a) / 2,
          m: (pa.m + pb.m) / 2,
          n: (pa.n + pb.n) / 2,
        });
      }
    }
  }

  /**
   * ε enters F = −c·ε·√ρ and φ = ε·(a/r)^n linearly and nowhere else, so
   * scaling every ε (elemental and mixed) by `factor` scales the whole
   * energy surface and every force by exactly `factor`.
   */
  scaleEnergy(factor: number): void {
    for (const p of this.elemParams.values()) p.epsilon *= factor;
    for (const p of this.pairParams.values()) p.epsilon *= factor;
  }

  densityContribution(ai: Atom, aj: Atom, rsq: number, rCutSq: number) {
    const p = this.pairParams.get(pairKey(ai.elementSymbol, aj.elementSymbol));
    if (!p) return null;
    const r = Math.sqrt(rsq);
    const rc = Math.sqrt(rCutSq);
    // ψ(r) = (a/r)^m;  ψ'(r) = −m·ψ(r)/r;  shifted-force form
    //   ψ_sf(r) = ψ(r) − ψ(rc) − (r − rc)·ψ'(rc)
    // With m = 6 the linear term removes ~4 % of ρ at the NN distance, so
    // cohesion comes out short at our 5–7 Å cutoffs; that is repaired by
    // renormalizing ε at configure time (EamPack.cohesionEv), NOT by
    // switching to shifted-energy — measured 2026-10-06 on Cu slabs, the
    // shifted-energy ψ with the same normalized cohesion melted 50 K LOWER
    // (900→950 K vs 950→1000 K), i.e. farther from the published 1154 K.
    const psiR = Math.pow(p.a / r, p.m);
    const psiC = Math.pow(p.a / rc, p.m);
    const dpsiC = (-p.m * psiC) / rc;
    const psi = psiR - psiC - (r - rc) * dpsiC;
    return { psiOnI: psi, psiOnJ: psi };
  }

  embedding(a: Atom, rho: number) {
    const p = this.elemParams.get(a.elementSymbol);
    if (!p || rho <= 0) return { F: 0, dFdRho: 0 };
    const sqrtRho = Math.sqrt(rho);
    return {
      F: -p.c * p.epsilon * sqrtRho,
      dFdRho: (-p.c * p.epsilon) / (2 * sqrtRho),
    };
  }

  pairForce(ai: Atom, aj: Atom, rsq: number, rCutSq: number, dFdRhoI: number, dFdRhoJ: number) {
    const p = this.pairParams.get(pairKey(ai.elementSymbol, aj.elementSymbol));
    if (!p) return null;
    const r = Math.sqrt(rsq);
    const rc = Math.sqrt(rCutSq);

    // Pair part φ(r) = ε·(a/r)^n;  φ'(r) = −n·φ(r)/r;  shifted-force.
    const phiR = p.epsilon * Math.pow(p.a / r, p.n);
    const phiC = p.epsilon * Math.pow(p.a / rc, p.n);
    const dphiR = (-p.n * phiR) / r;
    const dphiC = (-p.n * phiC) / rc;
    const phi_sf = phiR - phiC - (r - rc) * dphiC;
    const dphi_sf = dphiR - dphiC;

    // Density gradient ψ'(r) = −m·(a/r)^m / r, shifted-force as well —
    // matches the shifted-force ψ used in the density pass so ρ and the
    // embedding force are derived from the same ψ_sf.
    const psiR = Math.pow(p.a / r, p.m);
    const psiC = Math.pow(p.a / rc, p.m);
    const dpsiR = (-p.m * psiR) / r;
    const dpsiC = (-p.m * psiC) / rc;
    const dpsi_sf = dpsiR - dpsiC;

    // ∂E/∂r_ij = φ'(r_ij) + (F'(ρ_i) + F'(ρ_j))·ψ'(r_ij)
    const dEdr = dphi_sf + (dFdRhoI + dFdRhoJ) * dpsi_sf;
    // Cartesian force on i = fScalar·(rᵢ−rⱼ);  fScalar = (−dE/dr)/r · GF.
    const fOverR = -dEdr / r;
    return { energy: phi_sf, fScalar: fOverR * GF_CONVERSION_CONSTANT };
  }
}

/** Finnis-Sinclair (1984) parameters for one element. */
export interface FinnisSinclairParams {
  /** Pair-function cutoff (Å). V(r) and V'(r) are zero at r = c by construction. */
  c: number;
  /** Density-function cutoff (Å). φ(r) and φ'(r) are zero at r = d. */
  d: number;
  /** Embedding amplitude (eV). */
  A: number;
  /** Cubic density-function correction (dimensionless). 0 for V/Mo/Ta/W; 1.8 for Fe/Cr in the 1984 paper. */
  beta: number;
  /** Pair-function quadratic polynomial coefficient c₀ (eV). */
  c0: number;
  /** Pair-function quadratic polynomial coefficient c₁ (eV/Å). */
  c1: number;
  /** Pair-function quadratic polynomial coefficient c₂ (eV/Å²). */
  c2: number;
}

/**
 * Finnis-Sinclair (1984), Phil. Mag. A50 139 — the canonical EAM-family
 * potential for BCC metals (V, Cr, Fe, Mo, Ta, W). Same E = Σ F(ρ) + Σ V(r)
 * shape as Sutton-Chen, with two key differences:
 *
 *   V(r) = (r − c)²·(c₀ + c₁·r + c₂·r²)   for r < c, 0 otherwise
 *   φ(r) = (r − d)² + β·(r − d)³/d        for r < d, 0 otherwise
 *   F(ρ) = −A·√ρ
 *
 * The polynomial pair function has its CUTOFF BUILT IN via the (r−c)²
 * factor — V and V' both vanish at r = c exactly, no shifted-force
 * needed. Same for φ at r = d. This is the FS advantage over Sutton-Chen
 * for cohesion: no truncation discontinuity, no ~10% cohesion offset
 * from cutoff shifting. The polynomial parameters are fit such that the
 * lattice constant, cohesion, and elastic constants all come out right
 * without any post-truncation correction.
 *
 * Two cutoffs per element (c for pair, d for density) — typically c < d.
 * In a pair where c ≤ r < d, only the density contribution is nonzero;
 * the pair part V is identically zero there.
 */
export class FinnisSinclairPotential implements ManyBodyPotential {
  readonly name = 'Finnis-Sinclair';
  private readonly params = new Map<string, FinnisSinclairParams>();

  constructor(params: Record<string, FinnisSinclairParams>) {
    for (const [el, p] of Object.entries(params)) this.params.set(el, p);
  }

  densityContribution(ai: Atom, aj: Atom, rsq: number, _rCutSq: number) {
    // _rCutSq is the kernel's σ-scaled cutoff (typically larger than d);
    // FS uses its own d as the density-function cutoff.
    const p = this.params.get(ai.elementSymbol);
    if (!p) return null;
    const r = Math.sqrt(rsq);
    if (r >= p.d) return null;
    // φ(r) = (r − d)² + β·(r − d)³/d.
    const dr = r - p.d;
    const dr2 = dr * dr;
    const phi = dr2 + (p.beta * dr2 * dr) / p.d;
    return { psiOnI: phi, psiOnJ: phi };
  }

  embedding(a: Atom, rho: number) {
    const p = this.params.get(a.elementSymbol);
    if (!p || rho <= 0) return { F: 0, dFdRho: 0 };
    const sqrtRho = Math.sqrt(rho);
    return { F: -p.A * sqrtRho, dFdRho: -p.A / (2 * sqrtRho) };
  }

  pairForce(ai: Atom, aj: Atom, rsq: number, _rCutSq: number, dFdRhoI: number, dFdRhoJ: number) {
    const p = this.params.get(ai.elementSymbol);
    if (!p) return null;
    const r = Math.sqrt(rsq);
    if (r >= p.c && r >= p.d) return null;

    // Pair part V(r) = (r − c)²·P(r),  P(r) = c₀ + c₁·r + c₂·r².
    // V'(r) = 2(r − c)·P(r) + (r − c)²·P'(r),   P'(r) = c₁ + 2·c₂·r.
    let V = 0;
    let dV = 0;
    if (r < p.c) {
      const rc = r - p.c;
      const P = p.c0 + p.c1 * r + p.c2 * r * r;
      const dP = p.c1 + 2 * p.c2 * r;
      V = rc * rc * P;
      dV = 2 * rc * P + rc * rc * dP;
    }

    // Density gradient φ'(r) = 2(r − d) + 3β·(r − d)²/d.
    let dphi = 0;
    if (r < p.d) {
      const dr = r - p.d;
      dphi = 2 * dr + (3 * p.beta * dr * dr) / p.d;
    }

    // ∂E/∂r_ij = V'(r_ij) + (F'(ρ_i) + F'(ρ_j))·φ'(r_ij)
    const dEdr = dV + (dFdRhoI + dFdRhoJ) * dphi;
    const fOverR = -dEdr / r;
    return { energy: V, fScalar: fOverR * GF_CONVERSION_CONSTANT };
  }
}

/** Cleri-Rosato (1993) TBSMA parameters for one element. */
export interface CleriRosatoParams {
  /** Pair-function prefactor (eV). */
  A: number;
  /** Density-function prefactor (eV) — ψ uses ξ². */
  xi: number;
  /** Pair-function exponent (dimensionless). */
  p: number;
  /** Density-function exponent (dimensionless). */
  q: number;
  /** Reference length scale (Å) — typically the nearest-neighbor distance. */
  r0: number;
}

/**
 * Cleri-Rosato (1993), Phys. Rev. B 48, 22 — the tight-binding second-
 * moment-approximation form, the EAM-family choice for HCP metals (and
 * an alternative for FCC/BCC). Same E = Σ F(ρ) + Σ V(r) shape as
 * Sutton-Chen and Finnis-Sinclair, with exponential rather than
 * power-law or polynomial functions:
 *
 *   V(r) = A · exp(−p·(r/r₀ − 1))           pair, exponential repulsion
 *   ψ(r) = ξ² · exp(−2q·(r/r₀ − 1))         density
 *   F(ρ) = −√ρ                              embedding (ξ² folded into ψ)
 *
 * Five parameters per element (A, ξ, p, q, r₀) fit to cohesion, lattice
 * constant, elastic moduli — Cleri-Rosato 1993 tabulates the FCC, BCC,
 * AND HCP metals from one consistent procedure. The exponential decay is
 * faster than Sutton-Chen's power law, so a moderate cutoff (~6 Å) is
 * plenty; shifted-force is applied to both V and ψ for energy
 * conservation at the cut.
 */
export class CleriRosatoPotential implements ManyBodyPotential {
  readonly name = 'Cleri-Rosato';
  private readonly params = new Map<string, CleriRosatoParams>();

  constructor(params: Record<string, CleriRosatoParams>) {
    for (const [el, p] of Object.entries(params)) this.params.set(el, p);
  }

  densityContribution(ai: Atom, aj: Atom, rsq: number, rCutSq: number) {
    const p = this.params.get(ai.elementSymbol);
    if (!p) return null;
    const r = Math.sqrt(rsq);
    const rc = Math.sqrt(rCutSq);
    // ψ(r) = ξ²·exp(−2q·(r/r₀−1)) shifted to vanish at the cutoff
    // (shifted-ENERGY, not shifted-force — the shifted-force linear
    // correction subtracts too much density for CR's slow exponential
    // decay, halving ρ at NN). Force step at the cutoff is small
    // because q is large enough that ψ' is tiny by then.
    const xi2 = p.xi * p.xi;
    const psiR = xi2 * Math.exp(-2 * p.q * (r / p.r0 - 1));
    const psiC = xi2 * Math.exp(-2 * p.q * (rc / p.r0 - 1));
    return { psiOnI: psiR - psiC, psiOnJ: psiR - psiC };
  }

  embedding(a: Atom, rho: number) {
    // F(ρ) = −√ρ;  F'(ρ) = −1/(2√ρ).  No element-specific prefactor —
    // the ξ² is in ψ (so ρ already carries it), giving F = −ξ·√Σexp(…).
    if (rho <= 0) return { F: 0, dFdRho: 0 };
    const sqrtRho = Math.sqrt(rho);
    return { F: -sqrtRho, dFdRho: -1 / (2 * sqrtRho) };
  }

  pairForce(ai: Atom, aj: Atom, rsq: number, rCutSq: number, dFdRhoI: number, dFdRhoJ: number) {
    const p = this.params.get(ai.elementSymbol);
    if (!p) return null;
    const r = Math.sqrt(rsq);
    const rc = Math.sqrt(rCutSq);

    // Pair V(r), shifted-energy (consistent with the ψ shift).
    const vR = p.A * Math.exp(-p.p * (r / p.r0 - 1));
    const vC = p.A * Math.exp(-p.p * (rc / p.r0 - 1));
    const v_se = vR - vC;
    // V'(r) = −(p/r₀)·V(r); raw (no shift on the derivative).
    const dV = (-p.p * vR) / p.r0;

    // Density gradient ψ'(r), raw (matches the shifted-energy ψ above).
    const xi2 = p.xi * p.xi;
    const psiR = xi2 * Math.exp(-2 * p.q * (r / p.r0 - 1));
    const dpsi = (-2 * p.q * psiR) / p.r0;

    // ∂E/∂r_ij = V'(r_ij) + (F'(ρ_i) + F'(ρ_j))·ψ'(r_ij)
    const dEdr = dV + (dFdRhoI + dFdRhoJ) * dpsi;
    const fOverR = -dEdr / r;
    return { energy: v_se, fScalar: fOverR * GF_CONVERSION_CONSTANT };
  }
}

/** Zhou–Johnson–Wadley (2004) EAM parameters for one element. */
export interface ZhouEamParams {
  /** Equilibrium nearest-neighbor distance (Å). */
  re: number;
  /** Density-function prefactor. */
  fe: number;
  /** Equilibrium host density ρe, and the ρs that scales the high-density branch of F. */
  rhoe: number;
  rhos: number;
  /** Pair-function exponents (repulsive α, attractive β — β is shared with the density function). */
  alpha: number;
  beta: number;
  /** Pair-function prefactors (eV). */
  A: number;
  B: number;
  /** Pair-function cutoff-shape parameters. */
  kappa: number;
  lambda: number;
  /** Embedding cubic on ρ < 0.85ρe (eV). */
  Fn: [number, number, number, number];
  /** Embedding cubic on 0.85ρe ≤ ρ < 1.15ρe (eV). */
  F: [number, number, number, number];
  /** Embedding on ρ ≥ 1.15ρe: Fe·[1 − η·ln(ρ/ρs)]·(ρ/ρs)^η. */
  eta: number;
  Fe: number;
}

/**
 * Zhou, Johnson & Wadley (2004), Phys. Rev. B 69, 144113 — the analytic
 * EAM fitted for 16 metals from one recipe (lattice constant, cohesion,
 * elastic constants, vacancy formation energy AND the Rose universal
 * equation of state, so the whole compression/expansion curve — not just
 * its curvature at equilibrium — is pinned). Same E = Σ F(ρ_i) + ½ Σ φ(r)
 * shape as the other EAM-family classes here:
 *
 *   φ(r) = A·e^(−α(r/re−1)) / (1 + (r/re − κ)^20)  −  B·e^(−β(r/re−1)) / (1 + (r/re − λ)^20)
 *   f(r) = fe·e^(−β(r/re−1)) / (1 + (r/re − λ)^20)
 *   F(ρ) = cubic in (ρ/ρn − 1) below 0.85ρe,  cubic in (ρ/ρe − 1) up to 1.15ρe,
 *          Fe·[1 − η·ln(ρ/ρs)]·(ρ/ρs)^η above.
 *
 * The (r/re − λ)^20 denominators are built-in smooth cutoffs: f and φ are
 * below 1e-4 of their nearest-neighbor values by 5 Å, so — unlike Sutton–
 * Chen's (a/r)^6 tail — the result does not depend on the kernel cutoff and
 * needs no cohesion renormalization. Shifted-energy at the kernel cutoff
 * only (the derivative there is ~1e-6 eV/Å; F = −∇V stays exact).
 *
 * Why it is here: Sutton–Chen Cu melts at ~950 K in our slabs (its own
 * coexistence T_m is ~1150 K) against the measured 1358 K; this form melts
 * at 1200–1300 K on the same slab — see SUTTON_CHEN_CU_PACK in
 * eamParameters.ts for the measured comparison.
 */
export class ZhouEamPotential implements ManyBodyPotential {
  // Short: it is the HUD chip's label next to 🌡 ⏱ ⚪ — the full attribution
  // (Zhou, Johnson & Wadley) is in the pack's `source`.
  readonly name = 'Zhou (2004)';
  private readonly params = new Map<string, ZhouEamParams>();

  constructor(params: Record<string, ZhouEamParams>) {
    for (const [el, p] of Object.entries(params)) this.params.set(el, p);
  }

  /** f(r) and f′(r) for one element. */
  private density(p: ZhouEamParams, r: number): { f: number; df: number } {
    const x = r / p.re;
    const e = Math.exp(-p.beta * (x - 1));
    const u = x - p.lambda;
    const u19 = Math.pow(u, 19);
    const den = 1 + u19 * u;
    const f = (p.fe * e) / den;
    // d/dr [fe·e/den] = fe·(e′·den − e·den′)/den² · (1/re),  e′ = −β·e, den′ = 20·u^19
    const df = (p.fe * (-p.beta * e * den - e * 20 * u19)) / (den * den * p.re);
    return { f, df };
  }

  /** φ(r) and φ′(r) for one element. */
  private pair(p: ZhouEamParams, r: number): { phi: number; dphi: number } {
    const x = r / p.re;
    const ea = Math.exp(-p.alpha * (x - 1));
    const ua = x - p.kappa;
    const ua19 = Math.pow(ua, 19);
    const dena = 1 + ua19 * ua;
    const eb = Math.exp(-p.beta * (x - 1));
    const ub = x - p.lambda;
    const ub19 = Math.pow(ub, 19);
    const denb = 1 + ub19 * ub;
    const phi = (p.A * ea) / dena - (p.B * eb) / denb;
    const dphi =
      (p.A * (-p.alpha * ea * dena - ea * 20 * ua19)) / (dena * dena * p.re) -
      (p.B * (-p.beta * eb * denb - eb * 20 * ub19)) / (denb * denb * p.re);
    return { phi, dphi };
  }

  densityContribution(ai: Atom, aj: Atom, rsq: number, rCutSq: number) {
    // Elemental only: cross-species pairs would need Zhou's
    // φ_ab = ½[(f_b/f_a)φ_aa + (f_a/f_b)φ_bb] rule, not implemented.
    if (ai.elementSymbol !== aj.elementSymbol) return null;
    const p = this.params.get(ai.elementSymbol);
    if (!p) return null;
    const r = Math.sqrt(rsq);
    const rc = Math.sqrt(rCutSq);
    const psi = this.density(p, r).f - this.density(p, rc).f;
    return { psiOnI: psi, psiOnJ: psi };
  }

  embedding(a: Atom, rho: number) {
    const p = this.params.get(a.elementSymbol);
    if (!p) return { F: 0, dFdRho: 0 };
    const rhon = 0.85 * p.rhoe;
    const rhoo = 1.15 * p.rhoe;
    if (rho < rhon) return cubicEmbedding(p.Fn, rho / rhon - 1, rhon);
    if (rho < rhoo) return cubicEmbedding(p.F, rho / p.rhoe - 1, p.rhoe);
    const y = rho / p.rhos;
    const lny = Math.log(y);
    const yEta = Math.pow(y, p.eta);
    return {
      F: p.Fe * (1 - p.eta * lny) * yEta,
      // d/dρ = Fe·η·y^(η−1)·[−1 + (1 − η·ln y)]/ρs = −Fe·η²·ln(y)·y^(η−1)/ρs
      dFdRho: (-p.Fe * p.eta * p.eta * lny * yEta) / (y * p.rhos),
    };
  }

  pairForce(ai: Atom, aj: Atom, rsq: number, rCutSq: number, dFdRhoI: number, dFdRhoJ: number) {
    if (ai.elementSymbol !== aj.elementSymbol) return null;
    const p = this.params.get(ai.elementSymbol);
    if (!p) return null;
    const r = Math.sqrt(rsq);
    const rc = Math.sqrt(rCutSq);
    const { phi, dphi } = this.pair(p, r);
    const phiC = this.pair(p, rc).phi;
    const { df } = this.density(p, r);
    // ∂E/∂r_ij = φ′(r_ij) + (F′(ρ_i) + F′(ρ_j))·f′(r_ij)
    const dEdr = dphi + (dFdRhoI + dFdRhoJ) * df;
    return { energy: phi - phiC, fScalar: (-dEdr / r) * GF_CONVERSION_CONSTANT };
  }
}

/** Σ c_i·x^i and its derivative with respect to ρ, where x = ρ/scale − 1. */
const cubicEmbedding = (c: [number, number, number, number], x: number, scale: number) => ({
  F: c[0] + x * (c[1] + x * (c[2] + x * c[3])),
  dFdRho: (c[1] + x * (2 * c[2] + x * 3 * c[3])) / scale,
});

// ─── Three-body (covalent) potentials ─────────────────────────────────
//
// Covalent crystals — diamond, Si, Ge, SiC, the III-V semiconductors —
// require an explicit angular term to maintain tetrahedral bonding. No
// pair potential (LJ, Buckingham, Morse) and no embedded-atom potential
// (EAM-family) reproduces directional sp³ bonding: they all prefer
// close-packed (FCC/HCP) over diamond cubic, and a diamond-Si lattice
// run on any pair-only potential collapses to a denser structure on the
// first thermal kick.
//
// The two canonical 3-body forms:
//   • Stillinger-Weber (1985)  — explicit 2-body + 3-body, angular term
//     penalises deviation from the tetrahedral angle 109.47°. Original
//     fit for Si; adapted sets exist for Ge, C, and the III-Vs.
//   • Tersoff (1986-89)        — bond-order: pair interaction modulated
//     by a coordination-environment factor b_ij that itself depends on
//     all atoms around i. More flexible (covers C, Si, Ge, SiC, BN,
//     GaN, AlN) but more complex chain rule for forces.
//
// `StillingerWeberPotential` is the first concrete implementation here.
// Tersoff would slot in as a second `ThreeBodyPotential` if/when broader
// covalent coverage is needed.
//
// The kernel dispatch (in CrystalPairInteractions) walks this in two
// passes: pair pass over (i,j) → V₂ + pair force; triple pass over each
// atom i's neighbor pairs (j,k) → V₃ + forces on i, j, k.
export interface ThreeBodyPotential {
  /** Short label for tooling (UI dropdown, logs). */
  readonly name: string;

  /** Pair-and-triple cutoff radius (Å); both terms vanish beyond this. */
  readonly cutoff: number;

  /**
   * Two-body term V₂(r_ij). Returns energy and `fScalar` so cartesian
   * force on atom i is fScalar·(rᵢ−rⱼ), pre-multiplied by
   * GF_CONVERSION_CONSTANT (same convention as PairPotential).
   * Returns null when r is beyond the pair cutoff.
   */
  pairTerm(ai: Atom, aj: Atom, rsq: number): { energy: number; fScalar: number } | null;

  /**
   * Three-body term V₃ for triple (i, j, k) with i as the CENTRAL atom.
   * `dij`/`dik` are displacement vectors from j/k to i (i.e. dij = rᵢ−rⱼ),
   * `rij`/`rik` are their magnitudes. Returns the V₃ energy and the
   * cartesian forces on the three atoms (each pre-multiplied by
   * GF_CONVERSION_CONSTANT). Returns null if either r is beyond cutoff.
   */
  tripleTerm(
    ai: Atom,
    aj: Atom,
    ak: Atom,
    dijx: number,
    dijy: number,
    dijz: number,
    rij: number,
    dikx: number,
    diky: number,
    dikz: number,
    rik: number,
  ): {
    energy: number;
    fix: number;
    fiy: number;
    fiz: number;
    fjx: number;
    fjy: number;
    fjz: number;
    fkx: number;
    fky: number;
    fkz: number;
  } | null;
}

/** Stillinger-Weber parameters for one element. */
export interface StillingerWeberParams {
  /** Energy scale, eV. */
  epsilon: number;
  /** Length scale, Å. */
  sigma: number;
  /** Pair prefactor A (dimensionless). */
  A: number;
  /** Pair Born-Mayer-like term coefficient B (dimensionless). */
  B: number;
  /** Pair power p. */
  p: number;
  /** Pair power q. */
  q: number;
  /** Cutoff in σ units; the SW pair and triple both vanish at r = a·σ. */
  a: number;
  /** Three-body strength λ (dimensionless). */
  lambda: number;
  /** Three-body exponent γ (dimensionless). */
  gamma: number;
  /** Cosine of the preferred bond angle (−1/3 for sp³ tetrahedral). */
  cosTheta0: number;
  /**
   * TMD r23 cutoff (Å). When set and > 0, the three-body V₃ is forced
   * to zero if the j-k distance |r_j − r_k| exceeds r23 — a third-leg
   * distance gate distinct from the i-j and i-k cutoffs.
   *
   * Used by the LAMMPS pair_sw_TMD variant for trigonal-prismatic TMDs
   * like MoS₂: each Mo has 6 S neighbors arranged as a trigonal prism,
   * with 9 close S-S pairs (~3.16 Å) and 6 cross-prism S-S pairs
   * (~4.78 Å). The two pair classes have very different S-Mo-S angles
   * (≈82° vs ≈136°), so a single-cosθ₀ SW would fit only one. Setting
   * r23 between them (e.g. 3.82 Å) excludes the cross-prism pairs from
   * the angular term, letting cos θ₀ ≈ 0.13 (the 82° value) fit cleanly.
   *
   * Leave undefined or 0 for standard SW (no third-leg cutoff). The
   * Si and Ge elemental packs don't set this; the MoS₂ pack does.
   */
  r23?: number;
}

/**
 * Stillinger-Weber (1985), J. Chem. Phys. 31 5262 — 2-body + explicit
 * 3-body potential for tetrahedral semiconductors. Original form for
 * elemental Si; multi-element extension (Zhou et al., LAMMPS) for the
 * II-VI semiconductors (CdTe, ZnS, ZnSe, ZnTe, HgS, …).
 *
 * Two-body (for the (i, j) bond):
 *   V₂(r_ij) = A_ij·ε_ij·[B_ij·(σ_ij/r_ij)^p − (σ_ij/r_ij)^q]
 *              · exp(σ_ij/(r_ij − a_ij·σ_ij))    for r_ij < a_ij·σ_ij
 *
 *   The exp factor is SW's "soft cutoff" — V₂ and V₂' both vanish
 *   smoothly as r → a·σ⁻, then are identically zero past it. No
 *   shifted-force needed; the form is self-truncating.
 *
 * Three-body (for the triple j-i-k with i central):
 *   V₃ = λ_jik·ε_jik · exp(γ_ij·σ_ij/(r_ij − a_ij·σ_ij)
 *                          + γ_ik·σ_ik/(r_ik − a_ik·σ_ik))
 *                    · (cos θ_jik − cosθ₀_jik)²
 *
 *   Penalises the angle ∠jik for deviating from the equilibrium cosθ₀
 *   (−1/3 for tetrahedral sp³). The exp factor uses the SAME soft
 *   cutoff as V₂. For each atom i, every unordered pair of neighbors
 *   (j, k) contributes V₃ once.
 *
 * Parameters are stored per (i, j, k) triple, matching the LAMMPS .sw
 * file format directly. The (i, j, j) diagonal carries the (i, j) pair
 * properties (ε, σ, A, B, p, q, a, γ); the (i, j, k) row for j ≠ k
 * carries the (j, i, k) triple-specific λ, ε, cosθ₀. The pair γ_ij and
 * σ_ij used in V₃'s i-j exponential come from the (i, j, j) diagonal;
 * γ_ik, σ_ik from (i, k, k).
 *
 * For an elemental pack (Si, Ge) a single (X, X, X) triple is enough.
 * For a binary II-VI (CdTe, ZnS, ZnSe, ZnTe) the 8 (i, j, k) triples
 * over {A, B} are needed — paste them in directly from a .sw file.
 */
export class StillingerWeberPotential implements ThreeBodyPotential {
  readonly name = 'Stillinger-Weber';
  readonly cutoff: number;
  /** Per (i, j, k) triple, keyed `${central}-${neighbor}-${third}`. */
  private readonly triples: Map<string, StillingerWeberParams>;

  constructor(triples: Record<string, StillingerWeberParams>) {
    this.triples = new Map(Object.entries(triples));
    // Cutoff = max over (i, j) diagonal rows of a·σ — beyond this no
    // pair contributes V₂, and no atom is a valid k in any ζ sum.
    let cutMax = 0;
    for (const [key, p] of this.triples.entries()) {
      const parts = key.split('-');
      if (parts[1] === parts[2]) {
        const c = p.a * p.sigma;
        if (c > cutMax) cutMax = c;
      }
    }
    this.cutoff = cutMax;
  }

  /** Triple lookup. Null if (central, j, k) isn't registered. */
  private getTriple(central: string, j: string, k: string): StillingerWeberParams | null {
    return this.triples.get(central + '-' + j + '-' + k) ?? null;
  }

  /** Pair diagonal = (i, j, j). Carries the (i, j) pair properties. */
  private getDiagonal(central: string, neighbour: string): StillingerWeberParams | null {
    return this.triples.get(central + '-' + neighbour + '-' + neighbour) ?? null;
  }

  pairTerm(ai: Atom, aj: Atom, rsq: number) {
    const p = this.getDiagonal(ai.elementSymbol, aj.elementSymbol);
    if (!p) return null;
    const aSigma = p.a * p.sigma;
    if (rsq >= aSigma * aSigma) return null;
    const r = Math.sqrt(rsq);
    const sOr = p.sigma / r;
    const sOrP = Math.pow(sOr, p.p);
    const sOrQ = p.q === 0 ? 1 : Math.pow(sOr, p.q);
    const f1 = p.A * p.epsilon * (p.B * sOrP - sOrQ);
    const denom = r - aSigma; // negative for r < aσ
    const f2 = Math.exp(p.sigma / denom);
    const energy = f1 * f2;
    // V₂'(r) = f₁'·f₂ + f₁·f₂'
    // f₁'(r) = A·ε·[−p·B·σ^p/r^(p+1) + q·σ^q/r^(q+1)] = (1/r)·A·ε·[−p·B·sOrP + q·sOrQ]
    const df1 = (p.A * p.epsilon * (-p.p * p.B * sOrP + p.q * sOrQ)) / r;
    // f₂'(r) = f₂·(−σ/(r−aσ)²)
    const df2 = (-f2 * p.sigma) / (denom * denom);
    const dV = df1 * f2 + f1 * df2;
    // Cartesian force on i = fScalar·(rᵢ−rⱼ),  fScalar = (−dV/dr)/r · GF.
    const fScalar = (-dV / r) * GF_CONVERSION_CONSTANT;
    return { energy, fScalar };
  }

  tripleTerm(
    ai: Atom,
    aj: Atom,
    ak: Atom,
    dijx: number,
    dijy: number,
    dijz: number,
    rij: number,
    dikx: number,
    diky: number,
    dikz: number,
    rik: number,
  ) {
    // dij = rᵢ − rⱼ, dik = rᵢ − rₖ. cos θ_jik = (dij·dik)/(rij·rik).
    // Pair (i-j) properties for the i-j exponential and cutoff.
    const eI = ai.elementSymbol;
    const pij = this.getDiagonal(eI, aj.elementSymbol);
    const pik = this.getDiagonal(eI, ak.elementSymbol);
    // Triple (i, j, k) row for λ, ε, cosθ₀.
    const pijk = this.getTriple(eI, aj.elementSymbol, ak.elementSymbol);
    if (!pij || !pik || !pijk) return null;

    const aSigmaIJ = pij.a * pij.sigma;
    const aSigmaIK = pik.a * pik.sigma;
    if (rij >= aSigmaIJ || rik >= aSigmaIK) return null;

    // TMD r23 gate — if the (j, k) third-leg distance exceeds the
    // triple-specific cutoff, the V₃ is identically zero. Used by MoS₂
    // (Kandemir 2016) to exclude cross-prism S-S pairs that would have
    // an angle the single cosθ₀ can't fit. r_jk² = |dik − dij|².
    // Discontinuous at r_jk = r23 — atoms shouldn't approach the
    // boundary in practice (MoS₂ has S-S close pairs at 3.16 Å while
    // r23 = 3.82 Å, leaving comfortable margin).
    if (pijk.r23 !== undefined && pijk.r23 > 0) {
      const djkx = dikx - dijx;
      const djky = diky - dijy;
      const djkz = dikz - dijz;
      const rjkSq = djkx * djkx + djky * djky + djkz * djkz;
      if (rjkSq > pijk.r23 * pijk.r23) return null;
    }

    const dotIJK = dijx * dikx + dijy * diky + dijz * dikz;
    const cosTheta = dotIJK / (rij * rik);
    const c = cosTheta - pijk.cosTheta0;
    const denomIJ = rij - aSigmaIJ; // negative
    const denomIK = rik - aSigmaIK;
    const xi = Math.exp((pij.gamma * pij.sigma) / denomIJ + (pik.gamma * pik.sigma) / denomIK);
    const energy = pijk.lambda * pijk.epsilon * xi * c * c;

    // gxIJ = γ_ij·σ_ij/((rij−a_ij·σ_ij)²·rij)  → ∂ξ/∂rⱼ_α = ξ·gxIJ·dijα
    // (sign from ∂rij/∂rⱼ = −dij/rij and ∂ξ/∂u = ξ).
    const gxIJ = (pij.gamma * pij.sigma) / (denomIJ * denomIJ * rij);
    const gxIK = (pik.gamma * pik.sigma) / (denomIK * denomIK * rik);
    // 2c·∂cos/∂rⱼ + c²·∂ξ/∂rⱼ → force on j  (with overall −λ_jik·ε_jik·c factor).
    //   ∂cosθ/∂rⱼ_α = −dikα/(rij·rik) + cosθ·dijα/rij²
    //   ∂ξ/∂rⱼ_α    = ξ·gxIJ·dijα
    // F_j = −λε·ξ·c·[c·gxIJ·dijα − 2·dikα/(rij·rik) + 2·cosθ·dijα/rij²]
    const pre = pijk.lambda * pijk.epsilon * xi * c;
    const invRijRik = 1 / (rij * rik);
    const invRij2 = 1 / (rij * rij);
    const invRik2 = 1 / (rik * rik);

    const jFactorIJ = c * gxIJ + 2 * cosTheta * invRij2;
    const jFactorIK = -2 * invRijRik;
    const kFactorIK = c * gxIK + 2 * cosTheta * invRik2;
    const kFactorIJ = -2 * invRijRik;

    const gf = GF_CONVERSION_CONSTANT;
    // F_j = -pre · (jFactorIJ · dij + jFactorIK · dik)
    const fjx = -pre * (jFactorIJ * dijx + jFactorIK * dikx) * gf;
    const fjy = -pre * (jFactorIJ * dijy + jFactorIK * diky) * gf;
    const fjz = -pre * (jFactorIJ * dijz + jFactorIK * dikz) * gf;
    // F_k = -pre · (kFactorIK · dik + kFactorIJ · dij)
    const fkx = -pre * (kFactorIK * dikx + kFactorIJ * dijx) * gf;
    const fky = -pre * (kFactorIK * diky + kFactorIJ * dijy) * gf;
    const fkz = -pre * (kFactorIK * dikz + kFactorIJ * dijz) * gf;
    // F_i = -(F_j + F_k) by Newton's third law within the triple.
    const fix = -fjx - fkx;
    const fiy = -fjy - fky;
    const fiz = -fjz - fkz;

    return { energy, fix, fiy, fiz, fjx, fjy, fjz, fkx, fky, fkz };
  }
}

// ─── Interlayer van der Waals (for layered covalent crystals) ─────────
//
// Layered covalent crystals — graphite, h-BN, MoS₂ — have strong sp²
// covalent bonding WITHIN each sheet and only weak van der Waals
// holding the sheets together. A short-range bond-order potential
// (Tersoff, SW, REBO) captures the in-plane physics perfectly but
// carries no long-range vdW, so under pure Tersoff the AB layers of
// graphite would simply dissociate during MD.
//
// `InterlayerLJ` is a Lennard-Jones overlay that runs ALONGSIDE the
// bond-order potential (not instead of). The kernel calls both: the
// bond-order pass handles short-range covalent bonding, this pass adds
// the long-range vdW contribution. A SWITCHING function gates the LJ
// to zero below `rSwitchLow` (≈ 2.5 Å for C — past the Tersoff C
// cutoff R+D = 2.1 Å) so the LJ never contaminates the bonded region.
// Without the switch, LJ at the in-plane NN distance (1.42 Å for C-C)
// would add a huge spurious repulsion and distort the sheet.
//
// The functional form is AIREBO's LJ contribution, simplified:
//   V_LJ(r) = S(r) · [4ε((σ/r)¹² − (σ/r)⁶)]_sf
// where S(r) is a cubic Hermite ramp from 0 at rSwitchLow to 1 at
// rSwitchHigh, and []_sf is the shifted-force LJ that vanishes at
// `rCutoff`. AIREBO's switch is more elaborate (uses a bond-order-
// dependent factor too); the cubic Hermite is the pedagogical core.
export class InterlayerLJ {
  readonly name = 'Interlayer-LJ';
  /** Per-element LJ parameters; cross-species via Lorentz-Berthelot mixing. */
  private readonly params: Map<string, { sigma: number; epsilon: number }>;
  /** Below this distance the LJ is identically zero (kept clear of bonded region). */
  readonly rSwitchLow: number;
  /** Above this distance the LJ is at full strength. */
  readonly rSwitchHigh: number;
  /** Outer cutoff; LJ is shifted-force to vanish at this distance. */
  readonly rCutoff: number;

  /**
   * Element symbol → row in `pairTable`. Filled lazily by `indexFor`; an
   * element without parameters gets −1 so the kernel can drop its atoms from
   * the Verlet list instead of asking about them every step.
   */
  private readonly elementIndex = new Map<string, number>();
  private readonly elements: string[] = [];
  /** Per element-pair constants (Lorentz-Berthelot mixed, cutoff terms baked in). */
  private pairTable: Array<Array<{ sigmaSq: number; epsilon: number; VLJc: number; dVLJc: number }>> = [];
  /** Outputs of the last successful `evaluateIndexed` — avoids an object per pair. */
  energyOut = 0;
  fScalarOut = 0;

  constructor(
    params: Record<string, { sigma: number; epsilon: number }>,
    rSwitchLow: number,
    rSwitchHigh: number,
    rCutoff: number,
  ) {
    this.params = new Map(Object.entries(params));
    this.rSwitchLow = rSwitchLow;
    this.rSwitchHigh = rSwitchHigh;
    this.rCutoff = rCutoff;
  }

  /** Row index of an element in the pair table, or −1 when it has no parameters. */
  indexFor(elementSymbol: string): number {
    const known = this.elementIndex.get(elementSymbol);
    if (known !== undefined) return known;
    if (!this.params.has(elementSymbol)) {
      this.elementIndex.set(elementSymbol, -1);
      return -1;
    }
    const idx = this.elements.length;
    this.elements.push(elementSymbol);
    this.elementIndex.set(elementSymbol, idx);
    // Extend the table by one row and one column.
    const rc = this.rCutoff;
    const rowFor = (a: string, b: string) => {
      // Lorentz-Berthelot mixing for cross-species. For homo-pairs (the
      // only case in graphite right now) it collapses to (σ_C, ε_C).
      const pa = this.params.get(a)!;
      const pb = this.params.get(b)!;
      const sigma = 0.5 * (pa.sigma + pb.sigma);
      const epsilon = Math.sqrt(pa.epsilon * pb.epsilon);
      const sigmaSq = sigma * sigma;
      // Raw LJ and its derivative at the cutoff (shifted-force correction).
      const sr2c = sigmaSq / (rc * rc);
      const sr6c = sr2c * sr2c * sr2c;
      const sr12c = sr6c * sr6c;
      return {
        sigmaSq,
        epsilon,
        VLJc: 4 * epsilon * (sr12c - sr6c),
        dVLJc: (-24 * epsilon * (2 * sr12c - sr6c)) / rc,
      };
    };
    for (let i = 0; i < idx; i++) this.pairTable[i].push(rowFor(this.elements[i], elementSymbol));
    this.pairTable.push(this.elements.map((e) => rowFor(elementSymbol, e)));
    return idx;
  }

  /**
   * Compute V_LJ_switched and its force-scalar for one pair.
   *   energy   – switched + shifted-force LJ contribution
   *   fScalar  – such that F_i = fScalar · (rᵢ − rⱼ), already × GF.
   * Returns null when the pair contributes nothing (r outside [rSwitchLow,
   * rCutoff] or either element lacks parameters).
   */
  evaluate(ai: Atom, aj: Atom, r: number, rsq: number): { energy: number; fScalar: number } | null {
    const ii = this.indexFor(ai.elementSymbol);
    const jj = this.indexFor(aj.elementSymbol);
    if (ii < 0 || jj < 0) return null;
    if (!this.evaluateIndexed(ii, jj, r, rsq)) return null;
    return { energy: this.energyOut, fScalar: this.fScalarOut };
  }

  /**
   * Same as `evaluate`, for atoms whose table rows (from `indexFor`) the
   * caller already holds. Writes `energyOut` / `fScalarOut` and returns
   * false when the pair contributes nothing. This is the kernel's hot loop:
   * ~120,000 pairs a step for a 960-atom triple-wall nanotube.
   */
  evaluateIndexed(ii: number, jj: number, r: number, rsq: number): boolean {
    if (r >= this.rCutoff || r < this.rSwitchLow) return false;
    const p = this.pairTable[ii][jj];

    // Switching function S(r) and its derivative dS/dr.
    // S = 0 for r ≤ rSwitchLow, smooth cubic to 1 by rSwitchHigh, 1 above.
    let S: number;
    let dS: number;
    if (r >= this.rSwitchHigh) {
      S = 1;
      dS = 0;
    } else {
      const width = this.rSwitchHigh - this.rSwitchLow;
      const t = (r - this.rSwitchLow) / width;
      S = t * t * (3 - 2 * t); // cubic Hermite: S(0)=0, S(1)=1, S'(0)=S'(1)=0
      dS = (6 * t * (1 - t)) / width;
    }

    // Raw LJ at r.
    const sr2 = p.sigmaSq / rsq;
    const sr6 = sr2 * sr2 * sr2;
    const sr12 = sr6 * sr6;
    const VLJ = 4 * p.epsilon * (sr12 - sr6);
    // dV_LJ/dr = −(24ε/r)·(2·sr12 − sr6); negative means attractive past well.
    const dVLJ = (-24 * p.epsilon * (2 * sr12 - sr6)) / r;

    // Shifted-force: V_sf(r) = V(r) − V(rc) − (r − rc)·V'(rc)
    // dV_sf/dr = V'(r) − V'(rc), vanishes at rc by construction.
    const VLJsf = VLJ - p.VLJc - (r - this.rCutoff) * p.dVLJc;
    const dVLJsf = dVLJ - p.dVLJc;

    // Total V_total(r) = S(r)·V_sf(r);  V'_total = S'·V_sf + S·V'_sf
    this.energyOut = S * VLJsf;
    const dVdr = dS * VLJsf + S * dVLJsf;

    // F = −dV/dr · r̂;  on i: fScalar · (rᵢ − rⱼ);  fScalar = −dV/dr / r · GF
    this.fScalarOut = (-dVdr / r) * GF_CONVERSION_CONSTANT;
    return true;
  }
}

// ─── Bond-order (Tersoff-family) potentials ───────────────────────────
//
// SW's `ThreeBodyPotential` interface decomposes the energy into a sum
// over triples (i, j, k). Tersoff's bond-order form does not factor
// that way: the energy of bond (i, j) couples to b_ij, which is itself
// a function of EVERY other atom k in i's coordination shell. You can't
// evaluate one bond without the whole environment.
//
// So this interface gives the potential atom i's entire neighbor list
// at once. The potential walks (i, j) for each neighbor j, sums the
// b_ij dependence over the remaining k's, and applies the chain-rule
// forces — on i, j, AND every k whose presence shapes b_ij. Newton's
// third law holds across the whole bond + environment, not per pair.
//
// The kernel side (CrystalPairInteractions.computeBondOrder) only does
// neighbor-list assembly; all physics — pair params, mixing rules,
// cutoff smoothing, the b_ij chain rule — lives in the potential.
export interface BondOrderPotential {
  /** Short label for tooling (UI dropdown, logs). */
  readonly name: string;

  /**
   * Whether atoms of this element take part. Optional: when absent every
   * non-guest atom does. A flexible graphene membrane has rim hydrogens the
   * carbon parameters do not cover; they are held by harmonic bonds instead.
   */
  handlesElement?(element: string): boolean;

  /**
   * Outer cutoff (Å); the kernel uses this to scope the neighbor
   * list. Beyond this distance no atom contributes to either V_ij or
   * to b_ij, so it is also the longest distance over which forces
   * propagate from this potential.
   */
  readonly cutoff: number;

  /**
   * Compute energy + forces for all bonds emanating from atom i, given
   * i's neighbor environment. The first `count` entries of `dx/dy/dz/r`
   * are the displacement (dij = rᵢ−rⱼ) and distance to each neighbor;
   * `neighbours[k]` is the matching Atom. Returns the energy contribution
   * for atom i's half-bonds (E_i = ½ Σⱼ V_ij), and applies cartesian
   * forces in-place to ai.force AND each neighbor's atom.force, already
   * scaled by GF_CONVERSION_CONSTANT and divided by atom mass (the same
   * convention the LJ / EAM / SW paths use).
   */
  evaluateAtom(
    ai: Atom,
    neighbours: ReadonlyArray<Atom>,
    dx: Float64Array,
    dy: Float64Array,
    dz: Float64Array,
    r: Float64Array,
    count: number,
  ): number;
}

/**
 * Tersoff parameters for ONE ordered (central, bonded) element pair.
 *
 * This is the full LAMMPS Tersoff .tersoff-file row contents (modulo
 * the e1/e2/e3 keys): the pair properties (A, B, λ₁, λ₂, R, D), the
 * bond-order properties (β, n), and the angular properties (c, d, h,
 * γ, λ₃, m) all live together per ordered (central, bonded) pair.
 *
 * Why per-pair rather than per-element + mixing rules: published
 * Tersoff binary parameters are NOT simple mixings of elemental ones —
 * each compound is a separate joint fit. LAMMPS stores parameters
 * directly per (i, j, k) triple for exactly this reason. For binary
 * compounds, the same element behaves differently when bonded to
 * itself vs the other species (different n, β, g function), so a
 * per-element table cannot represent it.
 *
 * For elemental crystals, only one pair entry is needed (Si-Si). For
 * binary compounds: four entries (Si-Si, C-C, Si-C, C-Si); the cross-
 * species ones can have different angular and bond-order parameters
 * by central atom, even though the pair (A, B, λ₁, λ₂, R, D) is
 * symmetric in the (i, j) pair direction.
 */
export interface TersoffParams {
  /** Repulsive pre-factor A, eV. */
  A: number;
  /** Attractive pre-factor B, eV. */
  B: number;
  /** Repulsive decay rate λ₁, Å⁻¹. */
  lambda1: number;
  /** Attractive decay rate λ₂, Å⁻¹. */
  lambda2: number;
  /** Bond-order pre-factor β. */
  beta: number;
  /** Bond-order exponent n. */
  n: number;
  /** Angular function strength c. */
  c: number;
  /** Angular function width d. */
  d: number;
  /** Preferred bond cosine h (= cos θ₀ at the minimum of g). */
  h: number;
  /** Cutoff midpoint R, Å. f_C(R) = 1/2. */
  R: number;
  /** Cutoff half-width D, Å. f_C is 1 for r < R−D, 0 for r > R+D. */
  D: number;
  /**
   * ω scale factor γ — multiplies the entire ω function. Original
   * Tersoff (1989) implies γ = 1. The Tersoff/Brenner generalization
   * (e.g. the LAMMPS GaN.tersoff) uses non-unit γ to scale the angular
   * contribution per pair.
   */
  gamma: number;
  /** Radial decay of the angular term, Å⁻¹. */
  lambda3: number;
  /** Exponent on (λ₃·Δr)^m inside the angular ω function (1 or 3). */
  m: number;
}

/**
 * Tersoff smooth cutoff function:
 *   f_C(r) = 1                                          for r ≤ R − D
 *          = 1/2 − 1/2·sin(π(r−R)/(2D))                 for R − D < r < R + D
 *          = 0                                          for r ≥ R + D
 * Both f_C and f_C' vanish at r = R+D (and f_C is C∞ in the interior
 * of the interpolation region), so V_ij and ∂V_ij/∂r both join smoothly
 * to zero at the cutoff — no truncation discontinuity, no shifted-force
 * correction needed.
 */
const tersoffCutoff = (r: number, R: number, D: number): number => {
  if (r <= R - D) return 1;
  if (r >= R + D) return 0;
  return 0.5 - 0.5 * Math.sin((Math.PI * (r - R)) / (2 * D));
};

/** Derivative of `tersoffCutoff`. */
const tersoffCutoffPrime = (r: number, R: number, D: number): number => {
  if (r <= R - D || r >= R + D) return 0;
  return (-Math.PI / (4 * D)) * Math.cos((Math.PI * (r - R)) / (2 * D));
};

/**
 * Tersoff (1989, PRB 39, 5566) bond-order potential — the standard
 * covalent potential for the carbon allotropes (diamond, graphite),
 * the group-IV semiconductors (Si, Ge), III-V semiconductors (BN, GaN,
 * AlN, the III-Vs via alloy mixing), and SiC. One functional form
 * covers sp³, sp², and intermediate hybridizations because the angular
 * preference is NOT fixed (as in SW) but emerges from the minimization
 * of g_i — so diamond's 109.47° and graphite's 120° both fall out of
 * the same parameter set when applied to the same composition.
 *
 *   V_ij = f_C(r_ij) · [A_ij·exp(−λ₁_ij·r_ij) − b_ij·B_ij·exp(−λ₂_ij·r_ij)]
 *   b_ij = (1 + (β_ij·ζ_ij)^n_ij)^(−1/(2 n_ij))
 *   ζ_ij = Σ_{k≠i,j} f_C(r_ik) · g_ij(θ_ijk) · ω_ij(r_ij − r_ik)
 *   g_ij(θ) = 1 + (c_ij/d_ij)² − c_ij²/(d_ij² + (h_ij − cos θ)²)
 *   ω_ij(Δr) = γ_ij · exp((λ₃_ij·Δr)^m_ij)
 *   E = ½ Σ_{i,j≠i} V_ij     (each ordered pair contributes V_ij/2)
 *
 * Parameters are stored per ordered (central, bonded) pair (key
 * `${central}-${bonded}`), matching the LAMMPS .tersoff file layout
 * directly. Both the bond-order parameters (β, n) and the angular
 * parameters (c, d, h, γ, λ₃, m) can differ across pairs — the central
 * atom's behavior bonded to itself is not the same as bonded to a
 * different species. The f_C(r_ik) cutoff inside the ζ sum uses the
 * (i, k) pair's R, D (not the (i, j) pair's), so a single Tersoff pack
 * can mix atoms of different sizes correctly.
 *
 * For an elemental crystal only one pair entry is needed (e.g. Si-Si).
 * For a binary, four entries are needed: A-A, B-B, A-B (A central),
 * B-A (B central). The A-B and B-A pair (A, B, λ₁, λ₂, R, D) parts are
 * physically symmetric — V_ij and V_ji at the same r contribute the same
 * "pair" energy — but the (β, n, c, d, h, γ, λ₃, m) parts naturally
 * differ because the central atom is different in each direction.
 *
 * Force chain rule: V_ij depends on positions of i, j, AND every k in
 * the b_ij sum through ζ_ij. Each (i, j, k) contributes three force
 * vectors per bond direction; the kernel walks ordered pairs (i, j)
 * with i central, so the (j, i) direction is computed separately and
 * carries its own b_ji over j's environment. Newton's third law is
 * satisfied by the translation-invariance of the energy expression.
 */
export class TersoffPotential implements BondOrderPotential {
  readonly name = 'Tersoff';
  readonly cutoff: number;
  /**
   * Keyed `${central}-${bonded}-${third}` — full per-triple lookup,
   * matching LAMMPS .tersoff file rows directly.
   *
   *   - For V_ij and b_ij (the bond properties + bond-order n, β), the
   *     "diagonal" row (i, j, j) is consulted: pair params A, B, λ₁,
   *     λ₂, R, D plus the central-atom's bond-order β, n.
   *   - For each k in the ζ_ij sum, the (i, j, k) row supplies the
   *     angular params c, d, h, γ, λ₃, m. Different LAMMPS files put
   *     the angular variation on (i, j) (BNC.tersoff) vs (i, k)
   *     (GaN.tersoff) vs (i,) alone (SiC.tersoff); the per-triple
   *     layout covers all three.
   *   - f_C(r_ik) inside the ζ sum uses the (i, k, k) diagonal's R, D
   *     — the i-k pair geometry, irrespective of which bond is being
   *     evaluated.
   */
  private readonly triples: Map<string, TersoffParams>;

  /**
   * @param triples per (central, bonded, third) parameters, keyed
   *   `${central}-${bonded}-${third}`. For elemental Si: one entry
   *   `Si-Si-Si`. For SiC: 8 entries (Si-Si-Si, Si-Si-C, Si-C-Si,
   *   Si-C-C, C-Si-Si, C-Si-C, C-C-Si, C-C-C). Each entry maps to a
   *   single LAMMPS .tersoff row — paste them in directly.
   */
  constructor(triples: Record<string, TersoffParams>) {
    this.triples = new Map(Object.entries(triples));
    // Outer cutoff = max over registered triples of (R+D); the kernel
    // uses it to size the per-atom neighbor scan.
    let cutMax = 0;
    for (const p of this.triples.values()) {
      const c = p.R + p.D;
      if (c > cutMax) cutMax = c;
    }
    this.cutoff = cutMax;
  }

  /** True when this parameter set covers the element (its X-X-X triple exists). */
  handlesElement(element: string): boolean {
    return this.triples.has(element + '-' + element + '-' + element);
  }

  /**
   * The registered rows, keyed `${central}-${bonded}-${third}` — read by
   * the pack composer in tersoffParameters.ts, which builds the table for
   * a layered stack (GaAs on AlAs) out of its binaries' tables.
   */
  rows(): ReadonlyMap<string, TersoffParams> {
    return this.triples;
  }

  /** Per-triple lookup. Returns null if the triple isn't registered. */
  private getTriple(central: string, bonded: string, third: string): TersoffParams | null {
    return this.triples.get(central + '-' + bonded + '-' + third) ?? null;
  }

  /** (i, j) diagonal — same as triple (i, j, j); used for V_ij and b_ij. */
  private getDiagonal(central: string, bonded: string): TersoffParams | null {
    return this.triples.get(central + '-' + bonded + '-' + bonded) ?? null;
  }

  evaluateAtom(
    ai: Atom,
    neighbours: ReadonlyArray<Atom>,
    dx: Float64Array,
    dy: Float64Array,
    dz: Float64Array,
    r: Float64Array,
    count: number,
  ): number {
    const eI = ai.elementSymbol;
    const gf = GF_CONVERSION_CONSTANT;
    const invMi = 1 / ai.mass;
    const fi = ai.force;
    let energy = 0;

    // Walk each j in i's neighbors. The (j, i) direction is handled
    // when the kernel later visits atom j — V_ji has its own b_ji over
    // j's environment and is not the same as V_ij.
    for (let nj = 0; nj < count; nj++) {
      const aj = neighbours[nj];
      const eJ = aj.elementSymbol;
      // Bond (i, j) properties — A, B, λ₁, λ₂, R, D, β, n — from the
      // (i, j, j) diagonal row.
      const pij = this.getDiagonal(eI, eJ);
      if (!pij) continue;
      const rij = r[nj];
      if (rij >= pij.R + pij.D) continue; // f_C(r_ij) = 0
      const dijx = dx[nj];
      const dijy = dy[nj];
      const dijz = dz[nj];

      const fcij = tersoffCutoff(rij, pij.R, pij.D);
      const fcijP = tersoffCutoffPrime(rij, pij.R, pij.D);
      const fR = pij.A * Math.exp(-pij.lambda1 * rij);
      const fA = pij.B * Math.exp(-pij.lambda2 * rij);
      const dfR = -pij.lambda1 * fR; // d f_R/dr
      const dfA = -pij.lambda2 * fA;

      // ── Pass 1: ζ_ij = Σ_k f_C(r_ik)·g_{ijk}(θ_jik)·ω_{ijk}(r_ij−r_ik) ──
      // The angular term uses the FULL per-triple (i, j, k) angular
      // params, NOT (i, j)'s diagonal alone. f_C uses the (i, k)
      // diagonal's R, D (k's geometric cutoff, not j's). The angular
      // can depend on j (BNC), on k (GaN), or be uniform per central
      // atom (SiC) — the per-triple lookup covers all three.
      // (Recomputed in pass 2 below for forces; the duplication is the
      // price of not allocating per-k scratch arrays.)
      let zeta = 0;
      for (let nk = 0; nk < count; nk++) {
        if (nk === nj) continue;
        const ak = neighbours[nk];
        const eK = ak.elementSymbol;
        const pikDiag = this.getDiagonal(eI, eK);
        if (!pikDiag) continue;
        const rik = r[nk];
        if (rik >= pikDiag.R + pikDiag.D) continue;
        const fcik = tersoffCutoff(rik, pikDiag.R, pikDiag.D);
        if (fcik <= 0) continue;
        const pijk = this.getTriple(eI, eJ, eK);
        if (!pijk) continue;
        const cosTheta = (dijx * dx[nk] + dijy * dy[nk] + dijz * dz[nk]) / (rij * rik);
        const hMc = pijk.h - cosTheta;
        const denom = pijk.d * pijk.d + hMc * hMc;
        const g = 1 + (pijk.c * pijk.c) / (pijk.d * pijk.d) - (pijk.c * pijk.c) / denom;
        const dr = rij - rik;
        const u = pijk.lambda3 * dr;
        // m is 1 or 3 in published Tersoff fits; Math.pow handles both,
        // and (λ₃=0)·anything = 0 so ω → γ·exp(0) = γ with no NaN.
        const um = pijk.m === 1 ? u : Math.pow(u, pijk.m);
        const omega = pijk.gamma * Math.exp(um);
        zeta += fcik * g * omega;
      }

      // ── b_ij and db_ij/dζ_ij ──
      // Compute (β·ζ)^n via exp(n·ln(βζ)) — for n ≈ 23 (Si T3) a
      // straight Math.pow can underflow on edge geometries; the
      // log/exp pair is well-conditioned.
      let bij: number;
      let dbDzeta: number;
      if (zeta <= 0) {
        // No neighbors other than j → isolated bond, bond order = 1.
        bij = 1;
        dbDzeta = 0;
      } else {
        const lnBz = Math.log(pij.beta * zeta);
        const u = Math.exp(pij.n * lnBz); // (β·ζ)^n
        const one_p_u = 1 + u;
        bij = Math.exp(-Math.log(one_p_u) / (2 * pij.n));
        // db/dζ = −b·u / [2·ζ·(1+u)]
        dbDzeta = (-bij * u) / (2 * zeta * one_p_u);
      }

      // ── Energy: V_ij/2 (each ordered pair contributes half) ──
      energy += 0.5 * fcij * (fR - bij * fA);

      // ── Direct pair force on i, j (b_ij held FIXED) ──
      //   ∂V_ij/∂r_ij = f_C'·(f_R − b·f_A) + f_C·(f_R' − b·f_A')
      // F_i = −½ · ∂V/∂r_ij · (dij/rij);  F_j = +½ · same.
      const dVdrDirect = fcijP * (fR - bij * fA) + fcij * (dfR - bij * dfA);
      const fDirScalar = (-0.5 * dVdrDirect) / rij;
      if (fi) {
        fi.x += fDirScalar * dijx * gf * invMi;
        fi.y += fDirScalar * dijy * gf * invMi;
        fi.z += fDirScalar * dijz * gf * invMi;
      }
      const fj = aj.force;
      const invMj = 1 / aj.mass;
      if (fj) {
        fj.x -= fDirScalar * dijx * gf * invMj;
        fj.y -= fDirScalar * dijy * gf * invMj;
        fj.z -= fDirScalar * dijz * gf * invMj;
      }

      // ── Chain forces through b_ij(ζ_ij) ──
      //   F_α^chain = +½ · f_C(r_ij)·f_A(r_ij) · db/dζ · ∂ζ_ij/∂r_α
      // (The +½ from energy convention combines with the − from F = −∂V
      // and the − inside ∂V/∂b_ij = −f_C·f_A to give a NET + here.)
      // chain_pref is the per-α scalar that multiplies ∂ζ/∂r_α.
      const chainPref = 0.5 * fcij * fA * dbDzeta;

      // Skip the chain pass entirely when there is no chain to apply —
      // saves the inner k-loop's work in the zero-zeta corner case.
      if (chainPref === 0) continue;

      // Accumulate ∂ζ_ij/∂r_i and ∂ζ_ij/∂r_j (sums over k); ∂ζ/∂r_k
      // is local to the current k and applied inside the loop.
      let dZIx = 0;
      let dZIy = 0;
      let dZIz = 0;
      let dZJx = 0;
      let dZJy = 0;
      let dZJz = 0;

      for (let nk = 0; nk < count; nk++) {
        if (nk === nj) continue;
        const ak = neighbours[nk];
        const eK = ak.elementSymbol;
        const pikDiag = this.getDiagonal(eI, eK);
        if (!pikDiag) continue;
        const rik = r[nk];
        if (rik >= pikDiag.R + pikDiag.D) continue;
        const fcik = tersoffCutoff(rik, pikDiag.R, pikDiag.D);
        if (fcik <= 0) continue;
        const pijk = this.getTriple(eI, eJ, eK);
        if (!pijk) continue;
        const fcikP = tersoffCutoffPrime(rik, pikDiag.R, pikDiag.D);
        const dikx = dx[nk];
        const diky = dy[nk];
        const dikz = dz[nk];

        const cosTheta = (dijx * dikx + dijy * diky + dijz * dikz) / (rij * rik);
        const hMc = pijk.h - cosTheta;
        const denom = pijk.d * pijk.d + hMc * hMc;
        const g = 1 + (pijk.c * pijk.c) / (pijk.d * pijk.d) - (pijk.c * pijk.c) / denom;
        // g'(cosθ) = −2·c²·(h − cosθ)/(d² + (h − cosθ)²)²
        const gP = (-2 * pijk.c * pijk.c * hMc) / (denom * denom);

        const dr = rij - rik;
        const u = pijk.lambda3 * dr;
        const um = pijk.m === 1 ? u : Math.pow(u, pijk.m);
        const omega = pijk.gamma * Math.exp(um);
        // ∂ω/∂(r_ij − r_ik), used to build vector gradients below.
        // For m=1: dω/du · λ₃ = ω · λ₃.
        // For m≠1: dω/du · λ₃ = ω · m · u^(m−1) · λ₃ — and u^(m−1) is
        // 0 at u=0 for m>1, so the whole derivative is 0 there.
        const domegaDrScalar =
          pijk.m === 1 ? omega * pijk.lambda3 : omega * pijk.m * Math.pow(u, pijk.m - 1) * pijk.lambda3;

        const invRij = 1 / rij;
        const invRik = 1 / rik;
        const invRijRik = invRij * invRik;
        const invRij2 = invRij * invRij;
        const invRik2 = invRik * invRik;

        // ∂term_k/∂r_j picks up TWO contributions:
        //   (a) cosθ-gradient: f_C·g'·ω · ∂cosθ/∂r_j
        //                      with ∂cosθ/∂r_j = −dik/(rij·rik) + cosθ·dij/rij²
        //   (b) ω-gradient:     f_C·g · ∂ω/∂r_j
        //                      with ∂ω/∂r_j = −domegaDrScalar · dij/rij
        // r_ik does not depend on r_j ⇒ no f_C' contribution to ∂/∂r_j.
        const cfGOmega = fcik * gP * omega; // multiplies ∂cosθ/∂r_α
        const cfGOmegaDr = fcik * g * domegaDrScalar; // multiplies ω-gradient vectors

        const dTjx = cfGOmega * (-dikx * invRijRik + cosTheta * dijx * invRij2) - cfGOmegaDr * dijx * invRij;
        const dTjy = cfGOmega * (-diky * invRijRik + cosTheta * dijy * invRij2) - cfGOmegaDr * dijy * invRij;
        const dTjz = cfGOmega * (-dikz * invRijRik + cosTheta * dijz * invRij2) - cfGOmegaDr * dijz * invRij;

        // ∂term_k/∂r_k picks up THREE contributions:
        //   (a) f_C-radial:    f_C'·g·ω · (−dik/rik)
        //   (b) cosθ-gradient: f_C·g'·ω · ∂cosθ/∂r_k
        //                      with ∂cosθ/∂r_k = −dij/(rij·rik) + cosθ·dik/rik²
        //   (c) ω-gradient:    f_C·g · ∂ω/∂r_k = +domegaDrScalar · dik/rik
        const fcPGOmega = fcikP * g * omega;
        const dTkx =
          -fcPGOmega * dikx * invRik +
          cfGOmega * (-dijx * invRijRik + cosTheta * dikx * invRik2) +
          cfGOmegaDr * dikx * invRik;
        const dTky =
          -fcPGOmega * diky * invRik +
          cfGOmega * (-dijy * invRijRik + cosTheta * diky * invRik2) +
          cfGOmegaDr * diky * invRik;
        const dTkz =
          -fcPGOmega * dikz * invRik +
          cfGOmega * (-dijz * invRijRik + cosTheta * dikz * invRik2) +
          cfGOmegaDr * dikz * invRik;

        // ∂term_k/∂r_i by translation invariance: term_k depends only on
        // (r_ij−r_ik) and r_ik and cosθ — all translation-invariant —
        // so Σ_α ∂term_k/∂r_α = 0. Saves recomputing the explicit form.
        const dTix = -dTjx - dTkx;
        const dTiy = -dTjy - dTky;
        const dTiz = -dTjz - dTkz;

        dZIx += dTix;
        dZIy += dTiy;
        dZIz += dTiz;
        dZJx += dTjx;
        dZJy += dTjy;
        dZJz += dTjz;

        // Apply chain force to atom k — only this k contributes to ∂ζ_ij/∂r_k.
        const fk = ak.force;
        if (fk) {
          const invMk = 1 / ak.mass;
          fk.x += chainPref * dTkx * gf * invMk;
          fk.y += chainPref * dTky * gf * invMk;
          fk.z += chainPref * dTkz * gf * invMk;
        }
      }

      // Apply chain forces to i and j (summed over all k).
      if (fi) {
        fi.x += chainPref * dZIx * gf * invMi;
        fi.y += chainPref * dZIy * gf * invMi;
        fi.z += chainPref * dZIz * gf * invMi;
      }
      if (fj) {
        fj.x += chainPref * dZJx * gf * invMj;
        fj.y += chainPref * dZJy * gf * invMj;
        fj.z += chainPref * dZJz * gf * invMj;
      }
    }

    return energy;
  }
}

/**
 * A primary potential that contributes nothing. Installed by the membrane
 * force field: the framework is pinned, so host–host pairs need no forces
 * (and plain LJ between carbons 1.42 Å apart would add hundreds of eV of
 * constant energy), while every gas pair — gas–sheet and gas–gas — goes
 * through the guest overlay, which this potential leaves switched on
 * because it is not named 'Lennard-Jones'.
 */
export class NullPairPotential implements PairPotential {
  readonly name = 'none';
  evaluate(): PairResult | null {
    return null;
  }
}
